// OrderConfirmation.js
import React, { useState, useEffect } from "react";
import {
  AlertCircle,
  Check,
  CreditCard,
  ArrowLeft,
  Loader,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";
import LoadingIndicator from "@/pages/LoadingIndicator";
import { usePanier } from "../homePage/PanierContext";
// import PayPalButton from "./PayPalButton ";

const BackendUrl = process.env.REACT_APP_Backend_Url;

// Service pour la gestion des messages
const AlertService = {
  showAlert(setSubmitStatus, message, type = "error") {
    setSubmitStatus({
      loading: false,
      error: type === "error" ? message : null,
      success: type === "success" ? message : null,
    });
  },
};

// Service pour la gestion des codes promo
const PromoCodeService = {
  async validateAndApply(codePromo, orderTotal, setSubmitStatus) {
    // Si le code promo a déjà été validé dans le panier, on utilise directement ses données
    if (!codePromo?.isValide) return { total: orderTotal, reduction: 0 };

    try {
      // Récupérer la réduction déjà calculée dans le panier
      const discount = parseFloat(localStorage.getItem("orderDiscount")) || codePromo.discount || 0;

      // Le total final est orderTotal (AVANT réduction) - discount, jamais négatif
      const reductionAppliquee = Math.min(discount, orderTotal);
      return {
        total: Math.round((orderTotal - reductionAppliquee) * 100) / 100,
        reduction: reductionAppliquee,
      };
    } catch (error) {
      console.error("Erreur validation code promo:", error);
      AlertService.showAlert(
        setSubmitStatus,
        "Erreur lors de la validation du code promo"
      );
      return { total: orderTotal, reduction: 0 };
    }
  },

  async invalidatePromoCode(codePromoId) {
    if (!codePromoId) return;

    try {
      // Le code promo sera invalidé/utilisé côté backend lors de la création de la commande
      localStorage.removeItem("orderCodeP");
    } catch (error) {
      console.error("Erreur invalidation code promo:", error);
    }
  },
};

const OrderConfirmation = ({ onClose }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState("delivery"); // ["delivery", "payment", "confirmation"]
  const [message, setMessage] = useState("");
  const [orderTotal, setOrderTotal] = useState(() => {
    const savedTotal = localStorage.getItem("orderTotal");
    return savedTotal ? parseFloat(savedTotal) : 0;
  });
  const [finalTotal, setFinalTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [orderCodeP, setOrderCodeP] = useState(() => {
    const savedCodeP = localStorage.getItem("orderCodeP");
    return savedCodeP ? JSON.parse(savedCodeP) : null;
  });

  const WEBHOOK_URL = `${process.env.REACT_APP_Backend_Url}/api/payments/paypal-webhook`;

  const [deliveryInfo, setDeliveryInfo] = useState({
    name: "",
    email: "",
    numero: "",
    region: "",
    quartier: "",
    description: "",
    appart: "",
    codePost: "",
  });
  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    error: null,
    success: null,
  });
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [orderReference, setOrderReference] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [com, setCom] = useState(null);

  // États pour Stripe
  const [succeeded, setSucceeded] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null);
  const { updatePanier, panier } = usePanier();
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    const shippingDetails = JSON.parse(localStorage.getItem("shippingDetails"));

    if (
      !shippingDetails ||
      Object.keys(shippingDetails).length === 0 ||
      !panier ||
      panier.length === 0
    ) {
      navigate("/panier");
    }
  }, []);
  // Gestionnaire des commandes
  const OrderManager = {
    async createOrUpdateOrder(orderData, existingOrder = null) {
      if (existingOrder) {
        orderData.id = existingOrder?.commandeId;
      }
      const endpoint = existingOrder
        ? `${BackendUrl}/api/ordersRoutes/updateCommande`
        : `${BackendUrl}/api/ordersRoutes/create`;

      const method = existingOrder ? "put" : "post";

      try {
        const response = await axios[method](endpoint, orderData);
        setCom(response.data.commande);
        localStorage.setItem(
          "pendingOrder",
          JSON.stringify({
            commandeId: response.data.commande._id,
            transactionId: orderReference,
            timestamp: new Date().getTime(),
          })
        );
        return response.data;
      } catch (error) {
        console.error("Erreur gestion commande:", error);
        return {
          error:
            error.response?.data?.message ||
            "Erreur lors de la création de la commande",
        };
      }
    },
  };
  // Appliquer le code promo au chargement
  useEffect(() => {
    const applyPromoCode = async () => {
      if (orderCodeP?.isValide) {
        const promoResult = await PromoCodeService.validateAndApply(
          orderCodeP,
          orderTotal,
          setSubmitStatus
        );
        setFinalTotal(promoResult.total);
        setDiscount(promoResult.reduction);
      } else {
        setFinalTotal(orderTotal);
        setDiscount(0);
      }
    };

    applyPromoCode();
  }, [orderTotal, orderCodeP]);

  // Utilitaire pour générer un ID de commande unique
  function generateUniqueID() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");

    return `${year}${month}${day}${hours}${minutes}${seconds}${random}`;
  }

  // Récupérer les informations de livraison enregistrées
  useEffect(() => {
    const fetchDeliveryInfo = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("userEcomme"));
        if (!user?.id) return;

        axios.defaults.headers.common["Authorization"] = `Bearer ${user.token}`;
        const response = await axios.get(
          `${BackendUrl}/api/shippingAddressRoutes/me`
        );

        if (response.data.address) {
          const address = response.data.address;
          setDeliveryInfo({
            name: address.name || "",
            email: address.email || "",
            numero: address.numero || "",
            region: address.region || "",
            quartier: address.quartier || "",
            description: address.description || "",
            appart: address.appart || "",
            codePost: address.codePost || "",
          });
        }
      } catch (error) {
        console.error("Erreur lors du chargement de l'adresse:", error);
      }
    };

    fetchDeliveryInfo();
  }, []);

  // Validation des informations de livraison
  const validateDeliveryInfo = () => {
    const errors = [];

    if (!deliveryInfo.name || deliveryInfo.name.length < 2) {
      errors.push("Le nom doit contenir au moins 2 caractères");
    }

    if (
      deliveryInfo.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(deliveryInfo.email)
    ) {
      errors.push("L'adresse email n'est pas valide");
    }

    if (!deliveryInfo.numero || deliveryInfo.numero.length < 8) {
      errors.push("Le numéro de téléphone doit contenir au moins 8 chiffres");
    }

    if (!deliveryInfo.region || deliveryInfo.region.length < 3) {
      errors.push("La région doit contenir au moins 3 caractères");
    }

    if (!deliveryInfo.quartier || deliveryInfo.quartier.length < 2) {
      errors.push("Le quartier doit contenir au moins 2 caractères");
    }
    if (!deliveryInfo.appart || deliveryInfo.appart.length < 2) {
      errors.push("L'appartement doit contenir au moins 2 caractères");
    }
    if (!deliveryInfo.codePost || deliveryInfo.codePost.length < 2) {
      errors.push("Le code postal n'est pas valide ");
    }

    return errors;
  };

  // Gestion des changements dans le formulaire de livraison
  const handleDeliveryChange = (e) => {
    const { name, value } = e.target;
    setDeliveryInfo((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Réinitialiser les messages d'erreur quand l'utilisateur modifie les champs
    if (submitStatus.error) {
      setSubmitStatus({
        loading: false,
        error: null,
        success: null,
      });
    }
  };

  // Gestionnaire pour le changement de méthode de paiement
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    // Réinitialiser les erreurs quand on change de méthode
    setError(null);
    setSubmitStatus({
      loading: false,
      error: null,
      success: null,
    });
  };

  // Gestionnaire pour les changements dans le formulaire de carte
  const handleCardElementChange = (event) => {
    setDisabled(event.empty);
    setError(event.error ? event.error.message : "");
  };

  const handlePaymentError = (error) => {
    setError(`Erreur de paiement: ${error.message || "Veuillez réessayer"}`);
  };

  const handlePaymentSuccess = (data) => {
    // setPaymentSuccess(true);
    // Rediriger vers la page de succès après un court délai
    setTimeout(() => {
      navigate(`/Commande?success=true`);
    }, 2000);
  };

  // Valider et passer à l'étape de paiement
  const handleProceedToPayment = async () => {
    setSubmitStatus({ loading: true, error: null, success: null });
    setMessage("Vérification des informations...");

    // Validation des informations de livraison
    const validationErrors = validateDeliveryInfo();
    if (validationErrors.length > 0) {
      AlertService.showAlert(setSubmitStatus, validationErrors.join(", "));
      setSubmitStatus({
        loading: false,
        error: validationErrors.join(", "),
        success: null,
      });
      return;
    }

    // Vérification du panier
    // const panier = JSON.parse(localStorage.getItem("panier"));
    if (!panier?.length || panier?.length <= 0) {
      AlertService.showAlert(setSubmitStatus, "Votre panier est vide");
      return;
    }

    try {
      // Sauvegarde de l'adresse de livraison
      const user = JSON.parse(localStorage.getItem("userEcomme"));
      if (!user?.id) {
        AlertService.showAlert(
          setSubmitStatus,
          "Veuillez vous connecter pour continuer"
        );
        return;
      }

      await axios.post(`${BackendUrl}/api/shippingAddressRoutes/create`, {
        ...deliveryInfo,
        email: deliveryInfo.email !== "" ? deliveryInfo.email : null,
        clefUser: user.id,
      });

      // Tout est OK, on passe à l'étape de paiement
      setCurrentStep("payment");
      setSubmitStatus({
        loading: false,
        error: null,
        success: "Informations de livraison validées",
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'adresse:", error);
      AlertService.showAlert(
        setSubmitStatus,
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde de l'adresse"
      );
    }
  };

  // Créer la commande mais sans finaliser le paiement
  const handleCreateOrder = async () => {
    setSubmitStatus({ loading: true, error: null, success: null });
    setMessage("Création de votre commande...");

    // 1. Validation initiale
    const user = JSON.parse(localStorage.getItem("userEcomme"));
    if (!user?.id) {
      AlertService.showAlert(
        setSubmitStatus,
        "Veuillez vous connecter pour continuer"
      );
      return null;
    }

    // 2. Vérification du panier
    // const panier = JSON.parse(localStorage.getItem("panier"));
    if (!panier?.length || panier?.length <= 0) {
      AlertService.showAlert(setSubmitStatus, "Votre panier est vide");
      return null;
    }

    try {
      // 3. Préparation des données de commande
      const transactionId = generateUniqueID();
      setOrderReference(transactionId);

      const orderData = {
        clefUser: user.id,
        nbrProduits: panier.map((item) => ({
          produit: item._id,
          quantite: item.quantity,
          tailles: item.sizes,
          couleurs: item.colors,
        })),
        prix: orderTotal, // Prix original avant réduction
        reduction: discount, // Montant de la réduction
        prixTotal: finalTotal, // Prix final après réduction
        statusPayment: "en cours",
        reference: transactionId,
        livraisonDetails: {
          customerName: deliveryInfo.name,
          email: deliveryInfo.email || null,
          region: deliveryInfo.region,
          quartier: deliveryInfo.quartier,
          numero: deliveryInfo.numero,
          description: deliveryInfo.description,
        },
        prod: panier,
        ...(orderCodeP?.isValide && {
          codePro: true,
          idCodePro: orderCodeP._id,
        }),
        shippingDetails: JSON.parse(localStorage.getItem("shippingDetails")),
      };
      const existingOrder =
        JSON.parse(localStorage.getItem("pendingOrder")) || null;
      // 4. Création de la commande
      const orderResult = await OrderManager.createOrUpdateOrder(
        orderData,
        existingOrder
      );
      if (orderResult.error) {
        AlertService.showAlert(setSubmitStatus, orderResult.error);
        return null;
      }

      setSubmitStatus({ loading: false, error: null, success: null });
      return orderResult.commande;
    } catch (error) {
      console.error("Erreur lors de la création de la commande:", error);
      AlertService.showAlert(
        setSubmitStatus,
        error.response?.data?.message ||
          "Erreur lors de la création de la commande"
      );
      return null;
    }
  };

  // Initialiser le paiement Stripe après la création de la commande
  const initializeStripePayment = async () => {
    // Créer d'abord la commande puis initialiser le paiement
    const commande = await handleCreateOrder();
    if (!stripe || !commande) {
      setSubmitStatus({ loading: false, error: null, success: null });
      return;
    }

    setSubmitStatus({ loading: true, error: null, success: null });
    setMessage("Initialisation du paiement...");

    try {
      const user = JSON.parse(localStorage.getItem("userEcomme"));

      // Créer l'intention de paiement avec Stripe
      const paymentResponse = await axios.post(
        `${BackendUrl}/api/payment/create-payment-intent`,
        {
          commandeId: commande._id,
          customer_email: deliveryInfo.email || user.email || "",
          userId: user.id,
        },
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (paymentResponse.data.success) {
        // Important: return le clientSecret au lieu de juste l'affecter
        // pour pouvoir l'utiliser immédiatement
        setClientSecret(paymentResponse.data.clientSecret);
        // console.log({ clientSecret: paymentResponse.data.clientSecret });
        setSubmitStatus({ loading: false, error: null, success: null });
        return paymentResponse.data.clientSecret;
      } else {
        AlertService.showAlert(
          setSubmitStatus,
          paymentResponse.data.message || "Erreur d'initialisation du paiement"
        );
        return false;
      }
    } catch (error) {
      console.error("Erreur lors de l'initialisation du paiement:", error);
      AlertService.showAlert(
        setSubmitStatus,
        error.response?.data?.message ||
          "Erreur lors de l'initialisation du paiement"
      );
      return false;
    }
  };

  // Fonction de confirmation modifiée pour accepter un clientSecret en argument
  const confirmStripePayment = async (secretToUse) => {
    const secret = secretToUse || clientSecret;

    if (secret && stripe && elements) {
      setProcessing(true);
      setSubmitStatus({ loading: true, error: null, success: null });
      setMessage("Traitement du paiement...");

      try {
        const result = await stripe.confirmCardPayment(secret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: deliveryInfo.name,
              email: deliveryInfo.email || undefined,
              phone: deliveryInfo.numero || undefined,
              address: {
                city: deliveryInfo.region || undefined,
                line1: deliveryInfo.quartier || undefined,
                line2: deliveryInfo.description || undefined,
              },
            },
          },
        });

        if (result?.paymentIntent?.status === "succeeded") {
          AlertService.showAlert(
            setSubmitStatus,
            "Paiement effectué avec succès !",
            "success"
          );

          updatePanier([]);
          localStorage.removeItem("orderTotal");
          localStorage.removeItem("paymentInfo");
          localStorage.removeItem("pendingOrder");
          localStorage.removeItem("shippingDetails");
          localStorage.removeItem("paymentInitiated");

          setTimeout(() => {
            setCurrentStep("confirmation");
          }, 800);
        } else if (result?.error) {
          AlertService.showAlert(
            setSubmitStatus,
            result.error.message || "Échec du paiement. Veuillez réessayer."
          );
        }
      } catch (error) {
        AlertService.showAlert(
          setSubmitStatus,
          "Une erreur est survenue lors du paiement. Veuillez réessayer."
        );
      } finally {
        setProcessing(false);
      }
    } else {
      console.error(
        "Impossible de confirmer le paiement : informations manquantes",
        { secret, hasStripe: !!stripe, hasElements: !!elements }
      );
      setSubmitStatus({ loading: false, error: "Impossible d'initialiser le paiement", success: null });
    }
  };

  const handleSuccess = (result) => {};

  const handlePaymentSubmit = async (e) => {
    if (e) e.preventDefault();

    // Pour le paiement par carte
    if (paymentMethod === "stripe") {
      // Si nous avons déjà un secret client, procéder directement au paiement
      if (clientSecret && stripe && elements) {
        await confirmStripePayment();
      } else {
        // Créer d'abord la commande puis initialiser le paiement
        console.log("nononoonnnnnnnnnnnnnnnnnnnnnnno");
      }
    }
  };

  const renderStepIndicator = () => (
    <div className="flex justify-center mb-6">
      <div className="flex items-center">
        <div
          className={`flex items-center justify-center w-8 h-8 rounded-full ${
            currentStep === "delivery" ||
            currentStep === "payment" ||
            currentStep === "confirmation"
              ? "bg-[#30A08B] text-white"
              : "bg-gray-200"
          }`}
        >
          1
        </div>
        <div
          className={`w-16 h-1 ${
            currentStep === "payment" || currentStep === "confirmation"
              ? "bg-[#30A08B]"
              : "bg-gray-200"
          }`}
        ></div>
        <div
          className={`flex items-center justify-center w-8 h-8 rounded-full ${
            currentStep === "payment" || currentStep === "confirmation"
              ? "bg-[#30A08B] text-white"
              : "bg-gray-200"
          }`}
        >
          2
        </div>
        <div
          className={`w-16 h-1 ${
            currentStep === "confirmation" ? "bg-[#30A08B]" : "bg-gray-200"
          }`}
        ></div>
        <div
          className={`flex items-center justify-center w-8 h-8 rounded-full ${
            currentStep === "confirmation"
              ? "bg-[#30A08B] text-white"
              : "bg-gray-200"
          }`}
        >
          3
        </div>
      </div>
    </div>
  );

  // Rendu conditionnel selon l'étape
  const renderStep = () => {
    switch (currentStep) {
      case "delivery":
        return (
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-[#B17236] border-b-2 border-[#30A08B] pb-2 mb-4">
              Informations de livraison
            </h2>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={deliveryInfo.name}
                  onChange={handleDeliveryChange}
                  className="mt-1 p-3 border border-gray-300 rounded-lg w-full"
                  placeholder="Votre nom complet"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={deliveryInfo.email}
                  onChange={handleDeliveryChange}
                  className="mt-1 p-3 border border-gray-300 rounded-lg w-full"
                  placeholder="Votre email"
                />
              </div>
              <div>
                <label
                  htmlFor="numero"
                  className="block text-sm font-medium text-gray-700"
                >
                  Numéro de téléphone *
                </label>
                <input
                  type="tel"
                  id="numero"
                  name="numero"
                  value={deliveryInfo.numero}
                  onChange={handleDeliveryChange}
                  className="mt-1 p-3 border border-gray-300 rounded-lg w-full"
                  placeholder="Votre numéro de téléphone"
                />
              </div>
              <div>
                <label
                  htmlFor="region"
                  className="block text-sm font-medium text-gray-700"
                >
                  Pays *
                </label>
                <input
                  type="text"
                  id="region"
                  name="region"
                  value={deliveryInfo.region}
                  onChange={handleDeliveryChange}
                  className="mt-1 p-3 border border-gray-300 rounded-lg w-full"
                  placeholder="Votre région"
                />
              </div>
              <div>
                <label
                  htmlFor="quartier"
                  className="block text-sm font-medium text-gray-700"
                >
                  Ville | Village | Commune *
                </label>
                <input
                  type="text"
                  id="quartier"
                  name="quartier"
                  value={deliveryInfo.quartier}
                  onChange={handleDeliveryChange}
                  className="mt-1 p-3 border border-gray-300 rounded-lg w-full"
                  placeholder="Votre quartier"
                />
              </div>
              <div>
                <label
                  htmlFor="appart"
                  className="block text-sm font-medium text-gray-700"
                >
                  Appart | Immeuble | Etage | Nom de résidence *
                </label>
                <input
                  type="text"
                  id="appart"
                  name="appart"
                  value={deliveryInfo.appart}
                  onChange={handleDeliveryChange}
                  className="mt-1 p-3 border border-gray-300 rounded-lg w-full"
                  placeholder="appart"
                />
              </div>
              <div>
                <label
                  htmlFor="codePost"
                  className="block text-sm font-medium text-gray-700"
                >
                  code Postal *
                </label>
                <input
                  type="text"
                  id="codePost"
                  name="codePost"
                  value={deliveryInfo.codePost}
                  onChange={handleDeliveryChange}
                  className="mt-1 p-3 border border-gray-300 rounded-lg w-full"
                  placeholder="Votre Code Postal"
                />
              </div>
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700"
                >
                  Instructions de livraison
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={deliveryInfo.description}
                  onChange={handleDeliveryChange}
                  rows="3"
                  className="mt-1 p-3 border border-gray-300 rounded-lg w-full"
                  placeholder="Instructions supplémentaires pour la livraison"
                ></textarea>
              </div>
            </div>
            <div className="mt-6">
              <motion.button
                onClick={handleProceedToPayment}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={submitStatus.loading}
                className={`bg-[#30A08B] text-white p-3 rounded-lg w-full shadow-md hover:bg-opacity-90 flex justify-center items-center ${
                  submitStatus.loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {submitStatus.loading ? (
                  <Loader className="h-5 w-5 animate-spin" />
                ) : (
                  "Continuer vers le paiement"
                )}
              </motion.button>
            </div>
          </div>
        );
      case "payment":
        return (
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center mb-4">
              <button
                onClick={() => setCurrentStep("delivery")}
                className="flex items-center text-[#30A08B] hover:underline"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Revenir aux informations
                de livraison
              </button>
            </div>

            <h2 className="text-xl font-semibold text-[#B17236] border-b-2 border-[#30A08B] pb-2 mb-4">
              Mode de paiement
            </h2>

            <div className="mb-4">
              <div className="flex items-center space-x-3 mb-3">
                <input
                  type="radio"
                  id="stripe"
                  name="paymentMethod"
                  value="stripe"
                  checked={paymentMethod === "stripe"}
                  onChange={() => handlePaymentMethodChange("stripe")}
                  className="h-4 w-4 text-[#30A08B] focus:ring-[#30A08B]"
                />
                <label htmlFor="stripe" className="flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-gray-500" />
                  Paiement par carte
                </label>
              </div>
            </div>

            {paymentMethod === "stripe" && (
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-3">
                  Informations de carte
                </h3>
                <div className="p-4 border border-gray-300 rounded-lg mb-4">
                  {!clientSecret ? (
                    <button onClick={initializeStripePayment}>
                      Initialiser le paiement
                    </button>
                  ) : (
                    <div>
                      <CardElement
                        options={{
                          style: {
                            base: {
                              fontSize: "16px",
                              color: "#424770",
                              "::placeholder": {
                                color: "#aab7c4",
                              },
                            },
                            invalid: {
                              color: "#9e2146",
                            },
                          },
                        }}
                        onChange={handleCardElementChange}
                      />
                    </div>
                  )}
                </div>
                {error && (
                  <div className="text-red-500 text-sm mb-3">{error}</div>
                )}
              </div>
            )}

            {/* Récapitulatif de la commande */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium mb-3">
                Récapitulatif de la commande
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Sous-total:</span>
                  <span>{orderTotal} €</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Réduction:</span>
                    <span>
                      {orderCodeP?.isWelcomeCode
                        ? `${orderCodeP.prixReduiction}% (${discount} €)`
                        : `${discount} €`}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-2">
                  <span>Total:</span>
                  <span>{finalTotal} €</span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <motion.button
                onClick={handlePaymentSubmit}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={
                  paymentMethod === "stripe" &&
                  (!stripe ||
                    disabled ||
                    processing ||
                    succeeded ||
                    submitStatus.loading)
                }
                className={`bg-[#30A08B] text-white p-3 rounded-lg w-full shadow-md hover:bg-opacity-90 flex justify-center items-center ${
                  (paymentMethod === "stripe" &&
                    (!stripe || disabled || processing || succeeded)) ||
                  submitStatus.loading
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
              >
                {processing || submitStatus.loading ? (
                  <Loader className="h-5 w-5 animate-spin" />
                ) : (
                  "Confirmer et payer"
                )}
              </motion.button>
            </div>
          </div>
        );
      case "confirmation":
        return (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Check className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Commande confirmée
              </h2>
              <p className="text-gray-600 mb-4">
                Merci pour votre commande! Un email de confirmation vous sera
                envoyé prochainement.
              </p>

              <div className="w-full p-4 bg-gray-50 rounded-lg mb-6">
                <h3 className="text-lg font-medium mb-3 text-[#B17236]">
                  Détails de la commande
                </h3>
                <div className="space-y-2 text-left">
                  <div className="flex justify-between">
                    <span className="font-medium">Numéro de commande:</span>
                    <span>{orderReference}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Total:</span>
                    <span>{finalTotal} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Statut:</span>
                    <span className="text-green-600 font-medium">Payée</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Livraison:</span>
                    <span>
                      {deliveryInfo.region}, {deliveryInfo.quartier}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={() => navigate("/Commande")}
                  className="px-4 py-2 bg-[#30A08B] text-white rounded-lg hover:bg-opacity-90"
                >
                  Voir mes commandes
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Retour à l'accueil
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Animation du cercle de chargement
  const spinnerStyle = {
    border: "4px solid rgba(0, 0, 0, 0.1)",
    borderTop: "4px solid #FFF",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    animation: "spin 1s linear infinite",
    margin: "auto",
  };

  return (
    <>
      {/* <LoadingIndicator
        text={message?.length > 0 ? message : null}
        loading={submitStatus.loading ? true : false}
      > */}
      <div className="min-h-screen flex justify-center items-center">
        <div className="container max-w-4xl rounded-lg p-4 overflow-hidden">
          {/* Titres et instructions */}
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-[#B17236]">
              {currentStep === "delivery" && "Informations de livraison"}
              {currentStep === "payment" && "Paiement"}
              {currentStep === "confirmation" && "Confirmation"}
            </h1>
            <p className="text-gray-600 mt-2">
              {currentStep === "delivery" &&
                "Renseignez vos informations de livraison"}
              {currentStep === "payment" && "Choisissez votre mode de paiement"}
              {currentStep === "confirmation" &&
                "Votre commande a été confirmée avec succès"}
            </p>
          </div>

          {/* Indicateur d'étape */}
          {renderStepIndicator()}

          {/* Messages d'erreur et de succès */}
          {submitStatus.error && (
            <div className="mb-4 p-3 rounded bg-red-100 text-red-700">
              <p className="flex items-center">
                <AlertCircle className="mr-2 h-4 w-4" />
                {submitStatus.error}
              </p>
            </div>
          )}
          {submitStatus.success && (
            <div className="mb-4 p-3 rounded bg-green-100 text-green-700">
              <p className="flex items-center">
                <Check className="mr-2 h-4 w-4" />
                {submitStatus.success}
              </p>
            </div>
          )}

          {/* Contenu de l'étape actuelle */}
          {renderStep()}

          {/* Bouton d'annulation (uniquement pour les étapes delivery et payment) */}
          {(currentStep === "delivery" || currentStep === "payment") && (
            <button
              onClick={() => navigate("/panier")}
              className="mt-3 w-full p-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100"
              disabled={submitStatus.loading}
            >
              Annuler
            </button>
          )}

          {/* {com ? (
            <PayPalButton
              commande={com}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              webhookUrl={WEBHOOK_URL}
            />
          ) : (
            <></>
          )} */}
        </div>
      </div>
      {/* </LoadingIndicator> */}
    </>
  );
};

export default OrderConfirmation;